import type { Trace, TraceChunk } from "../shared/types";

export type TraceStatus = "idle" | "running" | "grounded" | "fallback" | "error";
export type LatencyKind = "retrieval" | "first" | "generation";

export interface LatencyBar {
  stage: string;
  ms: number;
  kind: LatencyKind;
  /** Offset and width as percentages of the total (geometry for `--geo-*`). */
  left: number;
  width: number;
}

const FIRST_TOKEN = /first token/i;
const GENERATION = /full answer|generation|answer/i;
const PERCENT = 100;
const MIN_WIDTH = 1.2;
const DECIMALS = 1;

function kindOf(stage: string): LatencyKind {
  if (FIRST_TOKEN.test(stage)) return "first";
  if (GENERATION.test(stage)) return "generation";
  return "retrieval";
}

function round(value: number): number {
  const factor = 10 ** DECIMALS;
  return Math.round(value * factor) / factor;
}

/**
 * Waterfall: retrieval stages run back to back. "First token" and "Full answer" are measured
 * from the start of the request, so they are drawn from the end of retrieval to their mark
 * (values below the retrieval end are drawn from there with their own duration).
 */
export function latencyBars(latency: Trace["latency"]): { bars: LatencyBar[]; totalMs: number } {
  const pairs = latency
    .map((pair) => ({ stage: typeof pair[0] === "string" ? pair[0] : "", ms: typeof pair[1] === "number" ? pair[1] : 0 }))
    .filter((p) => p.stage && Number.isFinite(p.ms) && p.ms >= 0);
  let cursor = 0;
  const spans = pairs.map((p) => {
    const kind = kindOf(p.stage);
    if (kind !== "retrieval") return { ...p, kind, start: -1, end: -1 };
    const start = cursor;
    cursor += p.ms;
    return { ...p, kind, start, end: cursor };
  });
  const genStart = cursor;
  const placed = spans.map((p) => (p.start >= 0 ? p : { ...p, start: genStart, end: p.ms >= genStart ? p.ms : genStart + p.ms }));
  const totalMs = Math.max(...placed.map((p) => p.end), 0);
  const bars = placed.map((p) => ({
    stage: p.stage,
    ms: Math.round(p.ms),
    kind: p.kind,
    left: totalMs ? round((p.start / totalMs) * PERCENT) : 0,
    width: totalMs ? round(Math.max(((p.end - p.start) / totalMs) * PERCENT, MIN_WIDTH)) : 0,
  }));
  return { bars, totalMs };
}

export function traceStatus(options: { busy: boolean; trace: Trace | undefined; failed: boolean; hasMessage: boolean }): TraceStatus {
  if (options.busy) return "running";
  if (options.failed) return "error";
  if (!options.trace || !options.hasMessage) return "idle";
  return options.trace.grounded === false ? "fallback" : "grounded";
}

export function chunkCounts(chunks: readonly TraceChunk[]): { used: number; filtered: number } {
  const used = chunks.filter((c) => c.used).length;
  return { used, filtered: chunks.length - used };
}

/** Candidates in display order: used first, then by rerank score, then fused rank. */
export function orderChunks(chunks: readonly TraceChunk[]): TraceChunk[] {
  const score = (c: TraceChunk) => c.rerank_score ?? c.rrf ?? 0;
  return [...chunks].sort((a, b) => Number(b.used) - Number(a.used) || score(b) - score(a));
}

/** Rough token estimate for the system-instructions counter (≈ 4 characters per token). */
export function estimateTokens(text: string): number {
  const CHARS_PER_TOKEN = 4;
  return Math.ceil(text.trim().length / CHARS_PER_TOKEN);
}

export function formatScore(value: number | null | undefined, digits = 2): string {
  return value === null || value === undefined ? "—" : value.toFixed(digits);
}
